// La fenêtre de création d'un lien de partage (CDC E2 § 4 et § 5, décidé par Marc le 03/10).
//
// Elle DIT, avant tout clic, ce que le lien fait : toute personne qui l'a verra ce graphique
// pendant 14 jours, sans compte, et la page indique qu'il vient de l'observatoire de la commune
// (E2-5). Le lien n'est créé qu'au geste explicite « Créer le lien » ; il s'affiche alors avec sa
// date d'expiration, un bouton pour le copier et — sur téléphone — la feuille de partage native.
// Depuis le 05/10 (migration 0087), son adresse se retrouve aussi dans « Mes partages » tant
// qu'il est actif — la fenêtre ne le promet que si le serveur répond `recopiable` ; sinon elle
// demande de le copier tout de suite.
import { useEffect, useId, useRef, useState } from 'react'
import { Link2, X } from 'lucide-react'

import { copierLien, partagerLien } from './partager'
import { dateCourte, type LienCree } from './lien'

export interface FenetreLienProps {
  titre: string
  commune: string
  creer: () => Promise<LienCree>
  onClose: () => void
}

type Etat = { t: 'pret' } | { t: 'travail' } | { t: 'cree'; lien: LienCree; retour?: string }
  | { t: 'echec'; message: string }

export default function FenetreLien({ titre, commune, creer, onClose }: FenetreLienProps) {
  const [etat, setEtat] = useState<Etat>({ t: 'pret' })
  const boite = useRef<HTMLDivElement>(null)
  const champ = useRef<HTMLInputElement>(null)
  const titreId = useId()
  const monte = useRef(true)
  // Remis à vrai à chaque montage : en mode strict (développement), React monte, démonte puis
  // remonte — sans cette ligne, la fenêtre restait sur « Création… » alors que le lien était créé.
  useEffect(() => { monte.current = true; return () => { monte.current = false } }, [])
  const partageNatif = typeof navigator !== 'undefined' && typeof navigator.share === 'function'

  // Échap ferme ; le focus entre dans la fenêtre et y reste (mêmes règles que le panneau
  // « Comprendre ce chiffre ») ; à la fermeture, l'appelant rend le focus au bouton Partager.
  useEffect(() => {
    boite.current?.focus()
    const h = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { e.stopPropagation(); onClose(); return }
      if (e.key !== 'Tab' || !boite.current) return
      const cibles = boite.current.querySelectorAll<HTMLElement>(
        'button:not([disabled]), input, [href], [tabindex]:not([tabindex="-1"])')
      if (!cibles.length) return
      const premier = cibles[0], dernier = cibles[cibles.length - 1]
      if (e.shiftKey && (document.activeElement === premier || !boite.current.contains(document.activeElement))) {
        e.preventDefault(); dernier.focus()
      } else if (!e.shiftKey && document.activeElement === dernier) {
        e.preventDefault(); premier.focus()
      }
    }
    window.addEventListener('keydown', h)
    return () => window.removeEventListener('keydown', h)
  }, [onClose])

  const lancer = async () => {
    if (etat.t === 'travail') return
    setEtat({ t: 'travail' })
    try {
      const lien = await creer()
      if (monte.current) setEtat({ t: 'cree', lien })
    } catch (e) {
      if (monte.current) setEtat({ t: 'echec', message: e instanceof Error ? e.message : String(e) })
    }
  }

  useEffect(() => {
    if (etat.t === 'cree') champ.current?.select()
  }, [etat.t])

  const copier = async (lien: LienCree) => {
    const ok = await copierLien(lien.url)
    setEtat({ t: 'cree', lien, retour: ok ? 'Lien copié.' : 'Copie refusée par le navigateur : sélectionnez le lien et copiez-le.' })
  }
  const partager = async (lien: LienCree) => {
    const issue = await partagerLien(lien.url, `${lien.titre} — ${lien.commune}`)
    if (issue === 'copie') setEtat({ t: 'cree', lien, retour: 'Lien copié.' })
  }

  return (
    <div className="obs2-expl-voile" onClick={onClose}>
      <div className="obs2-lien" role="dialog" aria-modal="true" aria-labelledby={titreId}
        ref={boite} tabIndex={-1} onClick={e => e.stopPropagation()}>
        <header className="obs2-lien-tete">
          <h3 id={titreId}><Link2 size={15} aria-hidden="true" /> Partager par lien</h3>
          <button type="button" className="obs2-iconbtn" aria-label="Fermer" onClick={onClose}>
            <X size={16} aria-hidden="true" />
          </button>
        </header>
        <p className="obs2-lien-objet">{titre} — {commune}</p>
        <p className="obs2-lien-avertissement">
          Toute personne qui a ce lien verra ce graphique pendant 14 jours, sans compte. La page
          indique qu’il vient de l’observatoire de {commune}.
        </p>
        {etat.t !== 'cree' ? (
          <>
            <p className="obs2-lien-aide">
              Le visiteur voit le graphique tel qu’il est aujourd’hui, avec ses sources — rien
              d’autre de l’observatoire. Vous pourrez retirer le lien à tout moment depuis
              « Mes partages ».
            </p>
            {etat.t === 'echec' && <p className="obs2-lien-echec" role="alert">{etat.message}</p>}
            <div className="obs2-lien-actions">
              <button type="button" className="obs2-btn obs2-btn-primary" onClick={lancer}
                aria-busy={etat.t === 'travail'} aria-disabled={etat.t === 'travail' || undefined}>
                {etat.t === 'travail' ? 'Création…' : 'Créer le lien'}
              </button>
              <button type="button" className="obs2-btn obs2-btn-ghost" onClick={onClose}>Annuler</button>
            </div>
          </>
        ) : (
          <>
            <label className="obs2-lien-champ">
              <span>Lien valable jusqu’au {dateCourte(etat.lien.expire_le)}</span>
              <input ref={champ} type="text" readOnly value={etat.lien.url}
                onFocus={e => e.currentTarget.select()} />
            </label>
            {etat.lien.recopiable ? (
              <p className="obs2-lien-aide">
                Vous retrouverez ce lien dans « Mes partages » tant qu’il est actif : vous pourrez
                l’y recopier ou le retirer.
              </p>
            ) : (
              // Serveur sans la migration 0087 : l'adresse n'est pas conservée (revue codex P2).
              <p className="obs2-lien-aide">
                Conservez-le maintenant : TerObs ne pourra pas vous le réafficher. Vous pourrez le
                retirer depuis « Mes partages ».
              </p>
            )}
            <div className="obs2-lien-actions">
              <button type="button" className="obs2-btn obs2-btn-primary" onClick={() => copier(etat.lien)}>
                Copier le lien
              </button>
              {partageNatif && (
                <button type="button" className="obs2-btn obs2-btn-ghost" onClick={() => partager(etat.lien)}>
                  Partager…
                </button>
              )}
              <button type="button" className="obs2-btn obs2-btn-ghost" onClick={onClose}>Fermer</button>
            </div>
            {etat.retour && <p className="obs2-lien-retour" role="status">{etat.retour}</p>}
          </>
        )}
      </div>
    </div>
  )
}
